// ================ Action types ================ //

import { getUserNotificationsPublic, updateUserNotificationsViewedAt } from '../util/api';
import {
    getCustomNotificationViewedItems,
    setCustomNotificationViewedItems,
} from '../util/localStorage';
import { getUserCountry } from '../util/location';
import { acquireThrottle, releaseThrottle } from '../util/callThrottle';

export const DISABLE_SCROLLING = 'app/UI/DISABLE_SCROLLING';
export const INSERT_NOTIFICATION_MODAL_DATA = 'app/UI/INSERT_NOTIFICATION_MODAL_DATA';
export const DISCARD_NOTIFICATION_MODAL_DATA = 'app/UI/DISCARD_NOTIFICATION_MODAL_DATA';
export const HANDLE_CONFETTI_ANIMATION = 'app/UI/HANDLE_CONFETTI_ANIMATION';
export const TOGGLE_UI_COMPONENT_STATE = 'app/UI/TOGGLE_UI_COMPONENT_STATE';

export const REQUEST_CUSTOM_NOTIFICATIONS = 'app/UI/REQUEST_CUSTOM_NOTIFICATIONS';
export const REQUEST_CUSTOM_NOTIFICATIONS_FAILED = 'app/UI/REQUEST_CUSTOM_NOTIFICATIONS_FAILED';
export const SET_CUSTOM_NOTIFICATIONS = 'app/UI/SET_CUSTOM_NOTIFICATIONS';

export const TOGGLE_NOTIFICATION_FEED = 'app/UI/TOGGLE_NOTIFICATION_FEED';

// ================ Reducer ================ //

const initialState = {
    notificationFeedPopupOpen: false,
    userCustomNotifications: [],
    userCustomNotificationsRequestInProgress: false,
    userCustomNotificationsRequestFailed: false,
    disableScrollRequests: [],
    confettiAnimationInProgress: false,
    bottomMobileBarVisible: true,
    notificationModal: {
        show: false,
    },
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;

    switch (type) {
        case TOGGLE_NOTIFICATION_FEED: {
            return {
                ...state,
                notificationFeedPopupOpen: payload,
            };
        }
        case REQUEST_CUSTOM_NOTIFICATIONS: {
            return {
                ...state,
                userCustomNotificationsRequestInProgress: payload,
            };
        }
        case SET_CUSTOM_NOTIFICATIONS: {
            return {
                ...state,
                userCustomNotifications: payload,
                userCustomNotificationsRequestFailed: false,
            };
        }
        case REQUEST_CUSTOM_NOTIFICATIONS_FAILED: {
            return {
                ...state,
                userCustomNotificationsRequestFailed: true,
            };
        }

        case TOGGLE_UI_COMPONENT_STATE: {
            const [fieldName, fieldValue] = payload;
            return {
                ...state,
                [fieldName]: fieldValue,
            };
        }
        case HANDLE_CONFETTI_ANIMATION: {
            return {
                ...state,
                confettiAnimationInProgress: payload,
            };
        }
        case INSERT_NOTIFICATION_MODAL_DATA: {
            return {
                ...state,
                notificationModal:
                    typeof payload === 'object'
                        ? {
                              show: true,
                              ...(payload || {}),
                          }
                        : { show: false },
            };
        }
        case DISCARD_NOTIFICATION_MODAL_DATA: {
            return { ...state, notificationModal: { show: false } };
        }

        case DISABLE_SCROLLING: {
            const { componentId, disableScrolling } = payload;
            const disableScrollRequests = state.disableScrollRequests;
            const componentIdExists = disableScrollRequests.find(
                c => c.componentId === componentId
            );

            if (componentIdExists) {
                const disableScrollRequestArray = disableScrollRequests.map(c => {
                    return c.componentId === componentId ? { ...c, disableScrolling } : c;
                });
                return { ...state, disableScrollRequests: [...disableScrollRequestArray] };
            }

            const disableScrollRequestArray = [
                ...disableScrollRequests,
                { componentId, disableScrolling },
            ];
            return {
                ...state,
                disableScrollRequests: disableScrollRequestArray,
            };
        }

        default:
            return state;
    }
}

// ================ Action creators ================ //

export const manageDisableScrolling = (componentId, disableScrolling) => ({
    type: DISABLE_SCROLLING,
    payload: { componentId, disableScrolling },
});

export const insertNotificationModalData = payload => ({
    type: INSERT_NOTIFICATION_MODAL_DATA,
    payload,
});

export const discardNotificationModalData = () => ({
    type: DISCARD_NOTIFICATION_MODAL_DATA,
});

export const handleConfettinAnimation = payload => ({
    type: HANDLE_CONFETTI_ANIMATION,
    payload,
});

export const toggleUiComponentBooleanState = payload => ({
    type: TOGGLE_UI_COMPONENT_STATE,
    payload,
});

// ================ Selectors ================ //

export const isScrollingDisabled = state => {
    const { disableScrollRequests } = state.UI;
    return disableScrollRequests.some(r => r.disableScrolling);
};

// ================ Thunks ================ //

/** notifications are polled on navigation, so keep two page changes apart */
export const CUSTOM_NOTIFICATIONS_INTERVAL = 2 * 60 * 1000;

export const getUserCustomNotifications = () => async (dispatch, getState, sdk) => {
    const { isAuthenticated } = getState().Auth;

    const viewedAsNonAuthed = getCustomNotificationViewedItems();
    const shouldViewedAsNonAuthedDataBeUpdated =
        isAuthenticated &&
        viewedAsNonAuthed &&
        Object.values(viewedAsNonAuthed).some(notification => !notification?.viewedAt);

    const userCountry = getUserCountry();
    const throttleKey = `UI/customNotifications/${userCountry}`;

    /**
     * Dispatched on every navigation, and the notification set rarely changes
     * within a session. Pending viewed-state is the exception: it has to be sent
     * up before it can be lost, so it bypasses the throttle.
     */
    if (
        !shouldViewedAsNonAuthedDataBeUpdated &&
        !acquireThrottle(throttleKey, CUSTOM_NOTIFICATIONS_INTERVAL)
    ) {
        return;
    }

    try {
        dispatch({ type: REQUEST_CUSTOM_NOTIFICATIONS, payload: true });

        if (shouldViewedAsNonAuthedDataBeUpdated) {
            /**
             * If a user previously viewed notifications being non-authed,
             * first, update his notificationsHistory data
             * and only after that request all notifications
             */
            await dispatch(requestToUpdateNotificationsAsViewedData(viewedAsNonAuthed));
            setCustomNotificationViewedItems(
                Object.fromEntries(
                    Object.keys(viewedAsNonAuthed).reduce(
                        (acc, key) => [...acc, [key, { viewedAt: new Date().toISOString() }]],
                        []
                    )
                )
            );
        }

        const response = await getUserNotificationsPublic(userCountry);
        const data = await response.json();

        if (data?.data) {
            dispatch({ type: SET_CUSTOM_NOTIFICATIONS, payload: data?.data });
        }
    } catch (e) {
        /** a failed poll should not hold the slot for the rest of the interval */
        releaseThrottle(throttleKey);
        dispatch({ type: REQUEST_CUSTOM_NOTIFICATIONS_FAILED });
    } finally {
        dispatch({ type: REQUEST_CUSTOM_NOTIFICATIONS, payload: false });
    }
};
/**
 * Mark notifications as viewed
 * @param {object} notificationsViewed - { [notificationId]: timestamp }
 * @returns
 */
export const requestToUpdateNotificationsAsViewedData = notificationsViewed => async (
    dispatch,
    getState
) => {
    try {
        const { userCustomNotifications } = getState().UI;

        dispatch({ type: REQUEST_CUSTOM_NOTIFICATIONS, payload: true });

        const viewedAt = new Date().toISOString();

        const getViewedAt = notification =>
            notificationsViewed[notification.id] || notificationsViewed[notification._id]
                ? { ...notification, viewedAt }
                : notification;

        dispatch({
            type: SET_CUSTOM_NOTIFICATIONS,
            payload: userCustomNotifications.map(getViewedAt),
        });

        const { error, message } = await updateUserNotificationsViewedAt({
            ids: Object.keys(notificationsViewed),
            viewedAt,
        });

        if (error) {
            throw new Error(message);
        }
    } catch (e) {
        dispatch({ type: REQUEST_CUSTOM_NOTIFICATIONS_FAILED });
    } finally {
        dispatch({ type: REQUEST_CUSTOM_NOTIFICATIONS, payload: false });
    }
};
